import React, { useEffect, useState } from "react";
import { useI18n } from "../context/I18nContext";
import { api } from "../context/AuthContext";
import { money } from "../lib/format";
import { Input } from "./ui/input";
import { Trophy, Medal, Bank, Receipt, PiggyBank, Vault, Ticket } from "@phosphor-icons/react";
import { motion } from "framer-motion";

const RANK_STYLES = [
  { ring: "ring-amber-300", badge: "bg-amber-400 text-amber-950", bar: "bg-amber-400", glow: "shadow-amber-200/60" },
  { ring: "ring-slate-300", badge: "bg-slate-300 text-slate-800", bar: "bg-slate-400", glow: "shadow-slate-200/60" },
  { ring: "ring-orange-300", badge: "bg-orange-400 text-orange-950", bar: "bg-orange-400", glow: "shadow-orange-200/60" },
];

const BreakdownChip = ({ icon: Icon, value }) =>
  value > 0 ? (
    <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
      <Icon size={12} weight="bold" /> <span className="font-mono-num">{money(value, "DOP")}</span>
    </span>
  ) : null;

export const BranchRankingPanel = () => {
  const { t } = useI18n();
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/branch-ranking", { params: { month } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [month]);

  const ranking = data?.ranking || [];
  const max = Math.max(1, ...ranking.map((r) => r.total || 0));

  return (
    <div data-testid="branch-ranking-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-2xl text-emerald-900 flex items-center gap-2">
            <Trophy size={26} weight="duotone" className="text-amber-500" /> {t("branch_ranking_title")}
          </h2>
          <p className="text-sm text-slate-500 mt-1">{t("branch_ranking_desc")}</p>
        </div>
        <div>
          <label className="block text-xs font-semibold text-emerald-900 mb-1">{t("ranking_month")}</label>
          <Input data-testid="ranking-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 w-44" />
        </div>
      </div>

      <div className="rounded-2xl bg-emerald-900 text-white p-5 mb-6 flex items-center justify-between" data-testid="ranking-grand-total">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-emerald-200/70">{t("ranking_total_collected")}</p>
          <p className="font-mono-num text-3xl font-bold mt-1">{money(data?.grand_total || 0, "DOP")}</p>
        </div>
        <Bank size={40} weight="duotone" className="text-emerald-300/70" />
      </div>

      {loading ? (
        <p className="text-slate-500 py-8">{t("loading")}</p>
      ) : ranking.length === 0 ? (
        <div className="text-center py-14 text-slate-400" data-testid="ranking-empty">
          <Trophy size={44} weight="duotone" className="mx-auto mb-3 opacity-40" />
          <p>{t("ranking_empty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {ranking.map((r, i) => {
            const style = RANK_STYLES[i] || null;
            const pct = Math.round(((r.total || 0) / max) * 100);
            return (
              <motion.div
                key={r.branch}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                data-testid={`ranking-row-${r.branch}`}
                className={`bg-white rounded-xl border border-gray-100 p-4 shadow-sm ${style ? `ring-2 ${style.ring} ${style.glow} shadow-lg` : ""}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${style ? style.badge : "bg-emerald-50 text-emerald-700"}`}>
                    {i < 3 ? <Medal size={20} weight="fill" /> : i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-semibold text-emerald-900 truncate">{r.branch}</span>
                      <span className="font-mono-num font-bold text-emerald-800 shrink-0">{money(r.total, "DOP")}</span>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${style ? style.bar : "bg-emerald-500"}`} style={{ width: `${Math.max(pct, 4)}%` }} />
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      <span className="text-[11px] text-slate-400 font-mono-num">{r.count} {t("ranking_movements")}</span>
                      {r.tickets > 0 && <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-100 rounded-full px-2 py-0.5"><Ticket size={12} weight="fill" /> {r.tickets} {t("ranking_tickets")}</span>}
                      <BreakdownChip icon={Receipt} value={r.loan_payments} />
                      <BreakdownChip icon={PiggyBank} value={r.savings_deposits} />
                      <BreakdownChip icon={Vault} value={r.term_deposits} />
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};
